/**
 * BusinessMap.tsx — Mapa GRANDE de negocios de la bandeja/búsqueda (T-020).
 *
 * MapLibre GL JS (vía react-map-gl/maplibre). Objetivo: percibir el VOLUMEN y las
 * ubicaciones reales a un zoom MÁS ALEJADO, con PINES DE PUNTA FINA — no
 * "círculos gordos" que escondan los negocios.
 *
 *   - Negocio individual = PIN DE PUNTA FINA (gota DOM cuyo vértice inferior
 *     señala la coordenada exacta), coloreado por banda de Opportunity Score
 *     (verde/ámbar/rojo). Legible sobre claro (Positron) y oscuro (Dark Matter).
 *     Se renderizan como marcadores DOM (mismo lenguaje que MapZones) filtrados
 *     al viewport — MapLibre/DOM aguanta miles sin saturar pintando solo los
 *     visibles.
 *   - CLUSTERING ligero SOLO a vista muy amplia (país/continente): la fuente
 *     GeoJSON agrupa hasta `CLUSTER_MAX_ZOOM`; por encima (región/provincia ya)
 *     se ven los pines individuales. Clúster = burbuja con su recuento.
 *   - Clic en un CLÚSTER → zoom in (getClusterExpansionZoom).
 *   - Clic en un PIN → abre la ficha (LeadSlideOver) vía onOpenLead.
 *   - Tiles CARTO (Positron claro / Dark Matter oscuro, sin API key) con
 *     etiquetas forzadas a español; conmuta con useTheme.
 *   - Negocio sin coords (latitude/longitude null) → no se pinta.
 *
 * Reutiliza el Opportunity Score (lib/opportunityScore) y el lenguaje visual de
 * pin de MapZones. La ficha se reutiliza tal cual desde el contenedor.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import MapGL, {
  Source,
  Layer,
  Marker,
  type MapRef,
  type MapLayerMouseEvent,
} from 'react-map-gl/maplibre'
import type { CircleLayerSpecification, SymbolLayerSpecification } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import type { InboxItem } from '@/services/listingsService'
import { opportunityScore, scoreBand, type OppBand } from '@/lib/opportunityScore'
import { useTheme } from '@/hooks/useTheme'

const STYLE_LIGHT = 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json'
const STYLE_DARK = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json'

const ES_LABEL = ['coalesce', ['get', 'name:es'], ['get', 'name:latin'], ['get', 'name']]

/** Semáforo de oportunidad por tema (MapLibre no resuelve CSS vars). */
const SEM = {
  light: { high: '#16a34a', mid: '#d97706', low: '#dc2626' },
  dark: { high: '#4ade80', mid: '#fbbf24', low: '#f87171' },
} as const

/** Hasta este zoom se agrupan los negocios en clústeres; por encima se ven los
 *  pines individuales (región/provincia). Compartido por la fuente y por la
 *  visibilidad de los marcadores DOM. */
const CLUSTER_MAX_ZOOM = 5
/** Tope de marcadores DOM pintados a la vez (los del viewport): evita saturar
 *  con datasets enormes; lo que excede ya está cubierto por el clustering al
 *  alejar. */
const MARKER_CAP = 1500

type RawMap = ReturnType<MapRef['getMap']>
function localizeLabelsToEs(map: RawMap): void {
  let layers
  try {
    layers = map.getStyle()?.layers
  } catch {
    return
  }
  if (!layers) return
  for (const layer of layers) {
    const tf = (layer as { layout?: Record<string, unknown> }).layout?.['text-field']
    if (tf === undefined) continue
    try {
      map.setLayoutProperty(layer.id, 'text-field', ES_LABEL as never)
    } catch {
      /* capa sin label editable */
    }
  }
}

const SOURCE_ID = 'mp-leads'

interface LeadPoint {
  place_id: string
  lng: number
  lat: number
  score: number
  band: OppBand
}

function buildPoints(items: InboxItem[]): LeadPoint[] {
  const out: LeadPoint[] = []
  for (const it of items) {
    if (typeof it.latitude !== 'number' || typeof it.longitude !== 'number') continue
    if (!Number.isFinite(it.latitude) || !Number.isFinite(it.longitude)) continue
    const sc = opportunityScore(it)
    out.push({
      place_id: it.place_id,
      lng: it.longitude,
      lat: it.latitude,
      score: sc.score,
      band: sc.band,
    })
  }
  return out
}

/**
 * Zoom inicial aproximado para encuadrar `bounds` en un viewport ~w×h (Web
 * Mercator simplificado). Se usa en `initialViewState` para que el PRIMER render
 * ya salga encuadrado (sin "flash" a zoom fijo y sin depender de un fitBounds
 * posterior); `fitBounds` luego lo afina. Clamp [3.5, 13] como el cap del fit.
 */
function zoomForBounds(
  b: [[number, number], [number, number]] | null,
  w = 1100,
  h = 560,
): number {
  if (!b) return 6
  const latSpan = Math.max(0.02, Math.abs(b[1][1] - b[0][1]))
  const lngSpan = Math.max(0.02, Math.abs(b[1][0] - b[0][0]))
  const zLng = Math.log2((360 * w) / (512 * lngSpan))
  const zLat = Math.log2((360 * h) / (512 * latSpan))
  return Math.max(3.5, Math.min(13, Math.min(zLng, zLat) - 0.4))
}

function pointsBounds(pts: LeadPoint[]): [[number, number], [number, number]] | null {
  if (pts.length === 0) return null
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity
  for (const p of pts) {
    if (p.lng < minLng) minLng = p.lng
    if (p.lat < minLat) minLat = p.lat
    if (p.lng > maxLng) maxLng = p.lng
    if (p.lat > maxLat) maxLat = p.lat
  }
  return [[minLng, minLat], [maxLng, maxLat]]
}

/** Pin de gota (SVG) coloreado por banda; el vértice inferior señala el punto. */
function PinTeardrop({ band }: { band: OppBand }) {
  return (
    <span className={`bm-pin ${band}`}>
      <svg viewBox="0 0 24 32" width={21} height={27} aria-hidden="true">
        <path d="M12 31.2C12 31.2 21.5 18.4 21.5 11A9.5 9.5 0 1 0 2.5 11C2.5 18.4 12 31.2 12 31.2Z" />
        <circle className="hole" cx="12" cy="11" r="3.4" />
      </svg>
    </span>
  )
}

export default function BusinessMap({
  items,
  onOpenLead,
  fill = false,
  height = 460,
}: {
  items: InboxItem[]
  onOpenLead: (item: InboxItem) => void
  /** Si true, ocupa todo el alto del contenedor (overlay a pantalla completa). */
  fill?: boolean
  height?: number
}) {
  const { theme } = useTheme()
  const mapRef = useRef<MapRef | null>(null)
  const sem = SEM[theme === 'dark' ? 'dark' : 'light']

  const points = useMemo(() => buildPoints(items), [items])
  const byId = useMemo(() => {
    const m = new Map<string, InboxItem>()
    for (const it of items) m.set(it.place_id, it)
    return m
  }, [items])
  const bounds = useMemo(() => pointsBounds(points), [points])
  const boundsKey = bounds ? bounds.flat().map((n) => n.toFixed(3)).join(',') : ''

  // Vista actual (zoom + bounds del viewport): decide qué pines DOM se pintan.
  const [view, setView] = useState<{
    zoom: number
    bounds: [[number, number], [number, number]] | null
  }>({ zoom: 0, bounds: null })

  const syncView = () => {
    const m = mapRef.current?.getMap()
    if (!m) return
    const b = m.getBounds()
    setView({
      zoom: m.getZoom(),
      bounds: [
        [b.getWest(), b.getSouth()],
        [b.getEast(), b.getNorth()],
      ],
    })
  }

  const geojson = useMemo(
    () => ({
      type: 'FeatureCollection' as const,
      features: points.map((p) => ({
        type: 'Feature' as const,
        geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
        properties: { place_id: p.place_id, score: p.score },
      })),
    }),
    [points],
  )

  const initialZoom = zoomForBounds(bounds)

  // Pines individuales = marcadores DOM, solo al pasar el zoom de clustering y
  // solo los del viewport (con margen) hasta el tope. Por debajo, los clústeres
  // (capa GeoJSON) representan el volumen.
  //
  // Hasta que el mapa emite su primer evento (load/move) usamos el zoom y los
  // bounds DERIVADOS DE LOS DATOS como fallback, para que los pines se pinten en
  // el primer render sin depender del timing de los eventos del mapa (clave para
  // que la captura no salga sin pines).
  const markers = useMemo(() => {
    const zoom = view.bounds ? view.zoom : initialZoom
    const vb = view.bounds ?? bounds
    if (zoom <= CLUSTER_MAX_ZOOM || !vb) return []
    const [[w, s], [e, n]] = vb
    const padX = (e - w) * 0.12
    const padY = (n - s) * 0.12
    const res: LeadPoint[] = []
    for (const p of points) {
      if (p.lng < w - padX || p.lng > e + padX || p.lat < s - padY || p.lat > n + padY) continue
      res.push(p)
      if (res.length >= MARKER_CAP) break
    }
    return res
  }, [points, view, bounds, initialZoom])

  const fit = () => {
    const map = mapRef.current
    if (!map || !bounds) return
    map.fitBounds(bounds, { padding: 56, maxZoom: 13, duration: 0 })
  }
  useEffect(() => {
    fit()
    syncView()
  }, [boundsKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoad = () => {
    const raw = mapRef.current?.getMap()
    if (!raw) return
    raw.resize() // el contenedor flex puede medir 0 al montar; re-mide el canvas
    localizeLabelsToEs(raw)
    raw.on('style.load', () => {
      const m = mapRef.current?.getMap()
      if (m) localizeLabelsToEs(m)
    })
    fit()
    syncView()
  }

  async function handleClick(e: MapLayerMouseEvent) {
    const f = e.features?.[0]
    if (!f) return
    const props = f.properties as Record<string, unknown>
    // Clúster → expandir (zoom in).
    if (props.cluster) {
      const map = mapRef.current?.getMap()
      const src = map?.getSource(SOURCE_ID) as
        | { getClusterExpansionZoom: (id: number) => Promise<number> }
        | undefined
      if (!map || !src) return
      try {
        const zoom = await src.getClusterExpansionZoom(props.cluster_id as number)
        const geom = f.geometry as unknown as { coordinates: [number, number] }
        map.easeTo({ center: geom.coordinates, zoom, duration: 500 })
      } catch {
        /* expansión no disponible */
      }
    }
  }

  // Color por banda de Opportunity Score (✅≥70 ⚠️40-69 ❌<40).
  const colorByScore = (scoreExpr: unknown) =>
    ['step', scoreExpr, sem.low, 40, sem.mid, 70, sem.high] as unknown

  const clusterLayer: CircleLayerSpecification = {
    id: 'mp-clusters',
    type: 'circle',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': colorByScore(['/', ['get', 'scoreSum'], ['get', 'point_count']]) as never,
      'circle-radius': ['step', ['get', 'point_count'], 15, 25, 20, 100, 26, 500, 32] as never,
      'circle-opacity': 0.9,
      'circle-stroke-width': 2,
      'circle-stroke-color': theme === 'dark' ? '#11161f' : '#ffffff',
    },
  }
  const clusterCountLayer: SymbolLayerSpecification = {
    id: 'mp-cluster-count',
    type: 'symbol',
    source: SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-font': ['Open Sans Bold'],
      'text-size': 12,
    },
    paint: { 'text-color': '#ffffff' },
  }

  if (points.length === 0) {
    return (
      <div className="mz-empty" style={{ height: fill ? '100%' : height }}>
        Sin negocios con coordenadas en esta búsqueda todavía.
      </div>
    )
  }

  const center = bounds
    ? { longitude: (bounds[0][0] + bounds[1][0]) / 2, latitude: (bounds[0][1] + bounds[1][1]) / 2 }
    : { longitude: -8.4, latitude: 43 }

  return (
    <div className="mz-wrap" style={{ height: fill ? '100%' : height }}>
      <MapGL
        ref={mapRef}
        initialViewState={{ longitude: center.longitude, latitude: center.latitude, zoom: initialZoom }}
        mapStyle={theme === 'dark' ? STYLE_DARK : STYLE_LIGHT}
        onLoad={handleLoad}
        onClick={handleClick}
        onMoveEnd={syncView}
        interactiveLayerIds={['mp-clusters']}
        attributionControl={{ compact: true }}
        dragRotate={false}
        touchZoomRotate={false}
        cursor="auto"
        style={{ width: '100%', height: '100%' }}
      >
        <Source
          id={SOURCE_ID}
          type="geojson"
          data={geojson}
          cluster
          // Clustering LIGERO solo a vista muy amplia: por encima de zoom 5
          // (región/provincia) los negocios ya se ven como pines individuales.
          clusterRadius={48}
          clusterMaxZoom={CLUSTER_MAX_ZOOM}
          clusterProperties={{ scoreSum: ['+', ['get', 'score']] }}
        >
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
        </Source>

        {markers.map((p) => (
          <Marker
            key={p.place_id}
            longitude={p.lng}
            latitude={p.lat}
            anchor="bottom"
            onClick={(e) => {
              e.originalEvent.stopPropagation()
              const item = byId.get(p.place_id)
              if (item) onOpenLead(item)
            }}
          >
            <PinTeardrop band={p.band} />
          </Marker>
        ))}
      </MapGL>
    </div>
  )
}
